<template>
  <TransitionRoot as="template" :show="open">
    <TransitionChild
      as="template"
      enter="transform transition ease-in-out duration-700"
      enter-from="translate-x-full"
      enter-to="translate-x-0"
      leave="transform transition ease-in-out duration-700"
      leave-from="translate-x-0"
      leave-to="translate-x-full"
    >
      <div class="pointer-events-auto fixed inset-y-0 right-0 z-60 w-screen max-w-md">
        <div class="flex h-full w-full flex-col divide-y divide-gray-200 bg-white shadow-xl">
          <div class="flex h-full flex-col overflow-y-scroll bg-white py-6">
            <div class="px-4 sm:px-6">
              <div class="flex items-start justify-between">
                <h1 class="text-base leading-6 font-semibold text-gray-900">
                  Network Latency Test
                </h1>
                <div class="ml-3 flex h-7 items-center">
                  <button
                    class="text-gray-400 hover:text-gray-500 focus:outline-none"
                    @click="$emit('close')"
                  >
                    <XIcon class="h-5 w-5" />
                  </button>
                </div>
              </div>
              <p class="mt-2 text-sm text-gray-600">
                Test the latency of API connections from your network
              </p>
            </div>

            <div class="relative mt-6 flex-1 px-4 sm:px-6">
              <div v-if="!testStarted" class="flex flex-col items-center py-8">
                <ServerIcon class="mb-4 h-16 w-16 text-gray-400" />
                <p class="mb-6 text-center text-sm text-gray-600">
                  Click the button below to test your network connection to Agentnoon's servers
                </p>
                <BaseButton
                  data-test="latency-test-run-button"
                  color="bluedark"
                  size="md"
                  :disabled="isTestRunning"
                  @click="runLatencyTest"
                >
                  {{ isTestRunning ? 'Testing...' : 'Run Network Test' }}
                </BaseButton>
              </div>

              <div v-else class="space-y-6">
                <div v-if="errorState" class="rounded-lg border border-red-200 bg-red-50 p-4">
                  <div class="flex">
                    <div class="flex-shrink-0">
                      <MinusCircleIcon class="h-5 w-5 text-red-400" />
                    </div>
                    <div class="ml-3">
                      <h3 class="text-sm font-medium text-red-800">Connection Error</h3>
                      <div class="mt-2 text-sm text-red-700">
                        <p>Unable to reach backend servers. This could be due to:</p>
                        <ul class="mt-2 list-disc space-y-1 pl-5">
                          <li>Corporate network restrictions</li>
                          <li>Security monitoring solutions blocking the connection</li>
                          <li>VPN or firewall settings</li>
                        </ul>
                        <div class="mt-3">
                          <p class="font-medium">Troubleshooting steps:</p>
                          <ol class="mt-1 list-decimal space-y-1 pl-5">
                            <li>Try connecting to a different network</li>
                            <li>Disable VPN if connected</li>
                            <li>Contact support@agentnoon.com for assistance</li>
                          </ol>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  v-else-if="hasSlowConnection"
                  class="rounded-lg border border-yellow-200 bg-yellow-50 p-4"
                >
                  <div class="flex">
                    <div class="flex-shrink-0">
                      <AlertTriangleIcon class="h-5 w-5 text-yellow-400" />
                    </div>
                    <div class="ml-3">
                      <h3 class="text-sm font-medium text-yellow-800">Slow Connection Detected</h3>
                      <div class="mt-2 text-sm text-yellow-700">
                        <p>One or more API endpoints are responding slowly (>5 seconds).</p>
                        <p class="mt-2">This may impact your experience. Possible causes:</p>
                        <ul class="mt-2 list-disc space-y-1 pl-5">
                          <li>Network congestion</li>
                          <li>Corporate security scanning</li>
                          <li>Geographic distance from servers</li>
                        </ul>
                        <div class="mt-3">
                          <p class="font-medium">Recommendations:</p>
                          <ol class="mt-1 list-decimal space-y-1 pl-5">
                            <li>Try a different network connection</li>
                            <li>Contact support@agentnoon.com if issue persists</li>
                          </ol>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  v-else-if="allTestsSuccessful"
                  class="rounded-lg border border-green-200 bg-green-50 p-4"
                >
                  <div class="flex">
                    <div class="flex-shrink-0">
                      <CheckCircleIcon class="h-5 w-5 text-green-400" />
                    </div>
                    <div class="ml-3">
                      <h3 class="text-sm font-medium text-green-800">Connection Healthy</h3>
                      <p class="mt-2 text-sm text-green-700">
                        All API endpoints are responding normally.
                      </p>
                    </div>
                  </div>
                </div>

                <div
                  v-else-if="
                    allTestsComplete && hasSkippedTests && !errorState && !hasSlowConnection
                  "
                  class="rounded-lg border border-blue-200 bg-blue-50 p-4"
                >
                  <div class="flex">
                    <div class="flex-shrink-0">
                      <InfoCircleIcon class="h-5 w-5 text-blue-400" />
                    </div>
                    <div class="ml-3">
                      <h3 class="text-sm font-medium text-blue-800">Partial Test Complete</h3>
                      <p class="mt-2 text-sm text-blue-700">
                        Some tests were skipped because no main board is available. Create or import
                        data to test all endpoints.
                      </p>
                    </div>
                  </div>
                </div>

                <div class="space-y-4">
                  <h3 class="text-sm font-semibold text-gray-900">API Endpoint Tests</h3>

                  <div
                    v-for="test in apiTests"
                    :key="test.name"
                    class="flex items-center justify-between border-b border-gray-100 py-3"
                  >
                    <div class="flex items-center space-x-3">
                      <div
                        v-if="test.status === 'pending'"
                        class="h-5 w-5 rounded-full bg-gray-200"
                      ></div>
                      <div v-else-if="test.status === 'testing'" class="h-5 w-5">
                        <RefreshIcon class="h-5 w-5 animate-spin text-blue-500" />
                      </div>
                      <div v-else-if="test.status === 'success'" class="h-5 w-5">
                        <CheckCircleIcon class="h-5 w-5 text-green-500" />
                      </div>
                      <div v-else-if="test.status === 'warning'" class="h-5 w-5">
                        <AlertTriangleIcon class="h-5 w-5 text-yellow-500" />
                      </div>
                      <div v-else-if="test.status === 'error'" class="h-5 w-5">
                        <MinusCircleIcon class="h-5 w-5 text-red-500" />
                      </div>
                      <div v-else-if="test.status === 'skipped'" class="h-5 w-5">
                        <InfoCircleIcon class="h-5 w-5 text-gray-400" />
                      </div>

                      <div>
                        <p class="text-sm font-medium text-gray-900">{{ test.label }}</p>
                        <p class="text-xs text-gray-500">{{ test.endpoint }}</p>
                      </div>
                    </div>

                    <div class="text-right">
                      <p v-if="test.latency !== null" class="text-sm font-medium">
                        <span v-if="test.status === 'error'" class="text-red-600">Failed</span>
                        <span
                          v-else
                          :class="{
                            'text-green-600': test.latency < 1000,
                            'text-yellow-600': test.latency >= 1000 && test.latency < 5000,
                            'text-red-600': test.latency >= 5000
                          }"
                        >
                          {{ formatLatency(test.latency) }}
                        </span>
                      </p>
                      <p v-else-if="test.status === 'testing'" class="text-xs text-gray-500">
                        Testing...
                      </p>
                      <p v-else-if="test.status === 'skipped'" class="text-xs text-gray-400">
                        Skipped
                      </p>
                      <p v-else class="text-xs text-gray-500">Pending</p>
                    </div>
                  </div>
                </div>

                <div class="mt-6 flex justify-between">
                  <BaseButton
                    data-test="latency-test-reset-button"
                    color="gray"
                    size="sm"
                    @click="resetTest"
                  >
                    Reset
                  </BaseButton>
                  <BaseButton
                    data-test="latency-test-rerun-button"
                    color="bluedark"
                    size="sm"
                    :disabled="isTestRunning"
                    @click="runLatencyTest"
                  >
                    {{ isTestRunning ? 'Testing...' : 'Run Again' }}
                  </BaseButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </TransitionChild>
  </TransitionRoot>
</template>

<script setup>
import { ref, computed } from 'vue'
import { TransitionRoot, TransitionChild } from '@headlessui/vue'
import BaseButton from '@/components/BaseButton.vue'
import { RefreshIcon, XIcon } from '@heroicons/vue/outline'
import ServerIcon from '@/assets/untitled-ui/server-03.svg'
import CheckCircleIcon from '@/assets/untitled-ui/check-circle.svg'
import AlertTriangleIcon from '@/assets/untitled-ui/alert-triangle.svg'
import MinusCircleIcon from '@/assets/untitled-ui/minus-circle.svg'
import InfoCircleIcon from '@/assets/untitled-ui/info-circle.svg'
import axios from 'axios'
import { API_URL, getAuthHeader } from '@/services/api.service.js'
import { useStore } from 'vuex'
import { logger } from '@/utils/logger'

defineProps({
  open: {
    type: Boolean,
    required: true
  }
})

defineEmits(['close'])

const store = useStore()
const testStarted = ref(false)
const isTestRunning = ref(false)
const errorState = ref(false)

// Get the main board ID for testing (use first master branch board)
const mainBoardId = computed(() => {
  const mainBoards = store.getters.mainBoards
  return mainBoards && mainBoards.length > 0 ? mainBoards[0].id : null
})

const apiTests = ref([
  {
    name: 'boards',
    label: 'Fetch Boards',
    endpoint: '/boards',
    status: 'pending',
    latency: null
  },
  {
    name: 'people',
    label: 'Fetch People',
    endpoint: '/people',
    status: 'pending',
    latency: null
  },
  {
    name: 'customFields',
    label: 'Fetch Custom Fields',
    endpoint: '/custom-fields',
    status: 'pending',
    latency: null
  },
  {
    name: 'settings',
    label: 'Fetch Settings',
    endpoint: '/settings/rate-card-mapping',
    status: 'pending',
    latency: null
  }
])

const hasSlowConnection = computed(() => {
  return apiTests.value.some(
    (test) => test.status === 'warning' || (test.latency !== null && test.latency >= 5000)
  )
})

const allTestsComplete = computed(() => {
  // Tests are complete when no test is pending or testing
  return (
    apiTests.value.every((test) => test.status !== 'pending' && test.status !== 'testing') &&
    testStarted.value
  )
})

const allTestsSuccessful = computed(() => {
  // All tests have succeeded (not skipped, not error)
  if (!testStarted.value) return false

  const completedTests = apiTests.value.filter(
    (test) => test.status === 'success' || test.status === 'warning'
  )

  // Check if all non-skipped tests are successful
  const nonSkippedTests = apiTests.value.filter((test) => test.status !== 'skipped')

  return (
    nonSkippedTests.length > 0 &&
    completedTests.length === nonSkippedTests.length &&
    !errorState.value
  )
})

const hasSkippedTests = computed(() => {
  return apiTests.value.some((test) => test.status === 'skipped')
})

const formatLatency = (ms) => {
  if (ms < 1000) {
    return `${ms.toFixed(0)}ms`
  }
  return `${(ms / 1000).toFixed(2)}s`
}

const measureLatency = async (apiCall) => {
  const startTime = performance.now()
  try {
    const result = await apiCall()
    const latency = performance.now() - startTime
    return { success: true, latency, result }
  } catch (error) {
    const latency = performance.now() - startTime
    return { success: false, latency, error }
  }
}

const makeApiCall = async (test) => {
  const apiUrl = await API_URL()
  const headers = await getAuthHeader()

  let url = `${apiUrl}${test.endpoint}`

  // Add main board ID to endpoints that need it
  if (test.endpoint === '/people' && mainBoardId.value) {
    url = `${apiUrl}/people/${mainBoardId.value}`
  } else if (test.endpoint === '/custom-fields' && mainBoardId.value) {
    url = `${apiUrl}/custom-fields/${mainBoardId.value}`
  } else if (test.endpoint === '/settings/rate-card-mapping' && mainBoardId.value) {
    url = `${apiUrl}/settings/${mainBoardId.value}/rate-card-mapping`
  }

  try {
    // Special handling for people endpoint which returns arraybuffer
    const config = {
      headers,
      timeout: 30000 // 30 second timeout
    }

    // People API returns compressed data as arraybuffer
    if (test.endpoint === '/people') {
      config.responseType = 'arraybuffer'
      config.headers['Content-Type'] = 'application/octet-stream'
    }

    const response = await axios.get(url, config)
    return response
  } catch (error) {
    logger.error('❌ [LatencyTest] Error for', test.name)
    logger.error('  - Error message:', error.message)
    logger.error('  - Error response:', error.response?.data)
    logger.error('  - Error status:', error.response?.status)
    logger.error('  - Full error:', error.message)
    throw error
  }
}

const runLatencyTest = async () => {
  testStarted.value = true
  isTestRunning.value = true
  errorState.value = false

  apiTests.value.forEach((test) => {
    test.status = 'pending'
    test.latency = null
  })

  for (const test of apiTests.value) {
    // Skip board-specific tests if we don't have a main board
    const needsBoard = test.endpoint !== '/boards'
    if (needsBoard && !mainBoardId.value) {
      test.status = 'skipped'
      test.latency = null
      test.error = 'No main board available'
      continue
    }

    test.status = 'testing'

    const result = await measureLatency(() => makeApiCall(test))

    if (!result.success) {
      test.status = 'error'
      test.latency = null
      errorState.value = true
    } else if (result.latency >= 5000) {
      test.status = 'warning'
      test.latency = result.latency
    } else {
      test.status = 'success'
      test.latency = result.latency
    }

    // Small delay between tests to avoid overwhelming the server
    await new Promise((resolve) => setTimeout(resolve, 300))
  }

  isTestRunning.value = false
}

const resetTest = () => {
  testStarted.value = false
  isTestRunning.value = false
  errorState.value = false
  apiTests.value.forEach((test) => {
    test.status = 'pending'
    test.latency = null
  })
}
</script>
